/** @format */
import { teamOperationErrorMessage } from "@api/project/team-operation-error";
import { teamProgramLinkId } from "@domain/project/team-policy";
import { SnackbarService } from "@domain/shared/snackbar.service";

import { DestroyRef, inject, Injectable, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { concatMap, map } from "rxjs";
import { LoggerService } from "@core/lib/services/logger/logger.service";
import { LeaveProjectUseCase } from "@api/project/use-cases/leave-project.use-case";
import { AppRoutes } from "@api/paths/app-routes";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Injectable()
export class DetailProjectInfoService {
  private readonly snackbar = inject(SnackbarService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly logger = inject(LoggerService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly leaveProjectUseCase = inject(LeaveProjectUseCase);

  readonly isTeamPage = signal<boolean>(false);
  readonly isVacanciesPage = signal<boolean>(false);
  readonly isProjectChatPage = signal<boolean>(false);
  readonly submissionProjectDateExpired?: boolean;
  readonly isProjectWorkSectionPage = signal<boolean>(false);
  readonly isKanbanBoardPage = signal<boolean>(false);
  readonly isGantDiagramPage = signal<boolean>(false);
  readonly isLeaveProjectModalOpen = signal<boolean>(false); // Флаг модального окна выхода
  readonly isEditDisable = signal<boolean>(false); // Флаг недоступности редактирования
  readonly isEditDisableModal = signal<boolean>(false); // Флаг недоступности редактирования для модалки
  readonly openSupport = signal<boolean>(false); // Флаг модального окна поддержки
  readonly leaderLeaveModal = signal<boolean>(false); // Флаг модального окна предупреждения лидера

  applySetIsDisabled(isSubmitted: boolean): void {
    this.isEditDisable.set(isSubmitted ?? false);
  }

  /**
   * Закрытие модального окна выхода из проекта
   */
  onCloseLeaveProjectModal(): void {
    this.isLeaveProjectModalOpen.set(false);
  }

  /**
   * Закрытие модального окна для невозможности редактировать проект
   */
  onUnableEditingProject(): void {
    if (this.isEditDisable()) {
      this.isEditDisableModal.set(true);
    } else {
      this.isEditDisableModal.set(false);
    }
  }

  /**
   * Выход из проекта
   */
  onLeave() {
    this.route.data
      .pipe(
        map(r => r["data"][0]),
        concatMap(p => {
          const selected = this.route.snapshot?.queryParamMap?.get("programLinkId");
          return this.leaveProjectUseCase.execute(
            p.id,
            teamProgramLinkId(p, selected ? Number(selected) : undefined),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        if (!result.ok) {
          const message = teamOperationErrorMessage(result.error.cause);
          if (message) this.snackbar.error(message);
          else
            this.snackbar.error(
              "Не удалось выйти из команды. Обновите страницу и попробуйте еще раз.",
            );
          return;
        }

        this.router
          .navigateByUrl(AppRoutes.projects.my())
          .then(() => this.logger.debug("Route changed from ProjectInfoComponent"));
      });
  }

  routingToMyProjects(): void {
    this.router.navigateByUrl(AppRoutes.projects.my());
  }

  applyUpdateStage(
    stage: "team" | "vacancies" | "chat" | "work-section" | "gant-diagram" | "kanban",
    isStage: boolean,
  ): void {
    switch (stage) {
      case "team":
        this.isTeamPage.set(isStage);
        break;

      case "chat":
        this.isProjectChatPage.set(isStage);
        break;

      case "gant-diagram":
        this.isGantDiagramPage.set(isStage);
        break;

      case "kanban":
        this.isKanbanBoardPage.set(isStage);
        break;

      case "vacancies":
        this.isVacanciesPage.set(isStage);
        break;

      case "work-section":
        this.isProjectWorkSectionPage.set(isStage);
        break;
    }
  }
}
